import { chatAuthHeaders, getChatApiBase } from "@/apiBase";
import type { ChatMessage } from "@/lib/chatApi";

export type ChatPollOption = {
  id: number;
  label: string;
  count: number;
  voter_ids: number[];
};

export type ChatPoll = {
  question: string;
  closed: boolean;
  total: number;
  my_option_id: number | null;
  last_voter_id?: number | null;
  options: ChatPollOption[];
};

export type ChatMessageWithPoll = ChatMessage & { poll?: ChatPoll | null };

export function messagePoll(m: ChatMessage | ChatMessageWithPoll | null | undefined): ChatPoll | null {
  if (!m) return null;
  const p = (m as ChatMessageWithPoll).poll;
  if (!p || typeof p.question !== "string" || !Array.isArray(p.options)) return null;
  return p;
}

export function isChatPollMessage(m: ChatMessage | ChatMessageWithPoll | null | undefined): boolean {
  return messagePoll(m) != null;
}

function chatPollsUrl(): string | null {
  const base = getChatApiBase();
  if (!base) return null;
  const absoluteBase = /^https?:\/\//i.test(base)
    ? base.replace(/\/?$/, "/")
    : `${typeof window !== "undefined" ? window.location.origin : "http://localhost"}${base.startsWith("/") ? base : `/${base}`}/`;
  return new URL("polls.php", absoluteBase).href;
}

async function postPoll<T>(
  suiteUserId: string | null,
  body: Record<string, unknown>
): Promise<{ success: true; data: T } | { success: false; message: string }> {
  const url = chatPollsUrl();
  if (!url) return { success: false, message: "API chat introuvable." };
  try {
    const res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { ...chatAuthHeaders(suiteUserId), "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = (await res.json()) as Record<string, unknown>;
    if (!res.ok || data.success === false) {
      return {
        success: false,
        message: typeof data.message === "string" ? data.message : "Action impossible.",
      };
    }
    return { success: true, data: data as T };
  } catch (e) {
    return { success: false, message: e instanceof Error ? e.message : "Erreur réseau" };
  }
}

export async function createChatPoll(
  suiteUserId: string | null,
  conversationId: number,
  question: string,
  options: string[]
): Promise<{ success: true; message: ChatMessageWithPoll } | { success: false; message: string }> {
  const r = await postPoll<{ message?: ChatMessageWithPoll }>(suiteUserId, {
    action: "create",
    conversation_id: conversationId,
    question,
    options,
  });
  if (!r.success) return r;
  const msg = r.data.message;
  if (!msg) return { success: false, message: "Réponse incomplète." };
  return { success: true, message: msg };
}

export async function voteChatPoll(
  suiteUserId: string | null,
  messageId: number,
  optionId: number
): Promise<{ success: true; message: ChatMessageWithPoll } | { success: false; message: string }> {
  const r = await postPoll<{ message?: ChatMessageWithPoll }>(suiteUserId, {
    action: "vote",
    message_id: messageId,
    option_id: optionId,
  });
  if (!r.success) return r;
  const msg = r.data.message;
  if (!msg) return { success: false, message: "Réponse incomplète." };
  return { success: true, message: msg };
}

export async function closeChatPoll(
  suiteUserId: string | null,
  messageId: number
): Promise<{ success: true; message: ChatMessageWithPoll } | { success: false; message: string }> {
  const r = await postPoll<{ message?: ChatMessageWithPoll }>(suiteUserId, {
    action: "close",
    message_id: messageId,
  });
  if (!r.success) return r;
  const msg = r.data.message;
  if (!msg) return { success: false, message: "Réponse incomplète." };
  return { success: true, message: msg };
}
